import { useEffect, useRef, useState, useCallback, useMemo } from "react";

// --- Constants ---
const DEFAULT_MIN_NDVI = -1.0;
const DEFAULT_MAX_NDVI = 0.92590492963791;
const USE_TMS = true;

const toDateKey = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const d = new Date(raw);
  return !isNaN(d.getTime()) ? d.toISOString().split("T")[0] : null;
};

// --- OPTIMIZATION: Look-Up Table (LUT) Generator ---
// Pre-calculates colors for all 256 possible grayscale values to avoid per-pixel math.
const createNdviLut = (filterMin, filterMax) => {
  const lut = new Uint8ClampedArray(256 * 4);
  const ALPHA = 240;

  for (let enc = 0; enc < 256; enc++) {
    const idx = enc * 4;
    if (enc === 255 || enc === 0) {
      lut[idx + 3] = 0; // Transparent
      continue;
    }

    const ndvi = DEFAULT_MIN_NDVI + (enc / 254.0) * (DEFAULT_MAX_NDVI - DEFAULT_MIN_NDVI);

    if (ndvi < filterMin || ndvi > filterMax) {
      lut[idx + 3] = 0; // Filtered out
      continue;
    }

    const v = Math.max(-1, Math.min(1, ndvi));
    const t = (v + 1) / 2;

    if (t < 0.5) {
      const f = t / 0.5;
      lut[idx] = 255;
      lut[idx + 1] = Math.round(255 * f);
      lut[idx + 2] = 0;
    } else {
      const f = (t - 0.5) / 0.5;
      lut[idx] = Math.round(255 * (1 - f));
      lut[idx + 1] = 255;
      lut[idx + 2] = 0;
    }
    lut[idx + 3] = ALPHA;
  }
  return lut;
};

const mercatorToLatLng = (x, y) => {
  const rMajor = 6378137;
  const shift = Math.PI * rMajor;
  const lon = (x / shift) * 180.0;
  let lat = (y / shift) * 180.0;
  lat = (180 / Math.PI) * (2 * Math.atan(Math.exp((lat * Math.PI) / 180.0)) - Math.PI / 2.0);
  return [lat, lon];
};

export default function NdviCesiumLayer({ viewer, latestSurvey, apiBase }) {
  const activeLayersRef = useRef([]);
  const hoverCanvasRef = useRef(null);

  // --- State for Filters ---
  const [filterMin, setFilterMin] = useState(DEFAULT_MIN_NDVI);
  const [filterMax, setFilterMax] = useState(DEFAULT_MAX_NDVI);

  // --- State for Progress Modal ---
  const [loading, setLoading] = useState(false);
  const [loadProgress, setLoadProgress] = useState(0);
  const tilesRequested = useRef(0);
  const tilesFinished = useRef(0);

  // Pre-calculate the LUT when filters change
  const currentLut = useMemo(() => createNdviLut(filterMin, filterMax), [filterMin, filterMax]);

  const updateProgress = () => {
    if (tilesRequested.current === 0) return;
    const prog = Math.round((tilesFinished.current / tilesRequested.current) * 100);
    setLoadProgress(prog);
    
    // Hide modal after a slight delay when finished
    if (prog >= 100) {
      setTimeout(() => {
        if (tilesFinished.current >= tilesRequested.current) {
          setLoading(false);
          setLoadProgress(0);
        }
      }, 800);
    }
  };

  // --- 1. Optimized Provider Factory ---
  const createNdviProvider = useCallback((urlTemplate, lut, creditText) => {
    const Cesium = window.Cesium;

    class NdviImageryProvider extends Cesium.UrlTemplateImageryProvider {
      constructor(options) {
        super(options);
        this.lut = options.lut;
      }

      requestImage(x, y, level) {
        setLoading(true);
        tilesRequested.current++;

        const yPos = USE_TMS ? ((1 << level) - y - 1) : y;
        const resourceUrl = this.url
          .replace("{z}", level)
          .replace("{x}", x)
          .replace("{y}", yPos);

        return Cesium.Resource.fetchImage({ url: resourceUrl }).then((image) => {
          const canvas = document.createElement('canvas');
          canvas.width = 256;
          canvas.height = 256;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          
          ctx.drawImage(image, 0, 0);
          const imageData = ctx.getImageData(0, 0, 256, 256);
          const data = imageData.data;
          const activeLut = this.lut;

          // ULTRA-FAST PIXEL LOOP: No math, just array indexing via LUT
          for (let i = 0; i < data.length; i += 4) {
            const lIdx = data[i] * 4;
            data[i]     = activeLut[lIdx];
            data[i + 1] = activeLut[lIdx + 1];
            data[i + 2] = activeLut[lIdx + 2];
            data[i + 3] = activeLut[lIdx + 3];
          }

          ctx.putImageData(imageData, 0, 0);
          tilesFinished.current++;
          updateProgress();
          return canvas;
        }).catch(() => {
          tilesFinished.current++;
          updateProgress();
          return document.createElement('canvas');
        });
      }
    }

    return new NdviImageryProvider({
      url: urlTemplate,
      maximumLevel: 24,
      tilingScheme: new Cesium.WebMercatorTilingScheme(),
      credit: new Cesium.Credit(creditText),
      lut: lut,
      hasAlphaChannel: true
    });
  }, []);

  const buildNdviUrl = (cand, latestSurvey, index) => {
    if (!latestSurvey?.location_name || !latestSurvey?.survey_time) return null;
    const loc = encodeURIComponent(String(latestSurvey.location_name));
    const sv = toDateKey(latestSurvey.survey_time);
    const rawLayerName = cand.layer_slug || cand.layer_name || `layer-${index}`;
    return `${apiBase}/ndvi/${loc}/${sv}/${encodeURIComponent(rawLayerName)}/tiles/{z}/{x}/{y}.png`;
  };

  const removeAllLayers = useCallback(() => {
    if (!viewer) return;
    activeLayersRef.current.forEach(item => {
      if (item.layer && !viewer.imageryLayers.isDestroyed()) {
        if (viewer.imageryLayers.contains(item.layer)) {
          viewer.imageryLayers.remove(item.layer, true);
        }
      }
    });
    activeLayersRef.current = [];
  }, [viewer]);

  const fetchLayerMetadata = async (tileUrlTemplate) => {
    if (!tileUrlTemplate) return null;
    try {
      const xmlUrl = tileUrlTemplate.replace(/\{z\}\/\{x\}\/\{y\}\.png$/, "tilemapresource.xml");
      const resp = await fetch(xmlUrl);
      if (!resp.ok) return null;
      const text = await resp.text();
      const bbox = new DOMParser().parseFromString(text, "text/xml").querySelector("BoundingBox");
      if (bbox) {
        const minx = parseFloat(bbox.getAttribute("minx"));
        const miny = parseFloat(bbox.getAttribute("miny"));
        const maxx = parseFloat(bbox.getAttribute("maxx"));
        const maxy = parseFloat(bbox.getAttribute("maxy"));
        const [lat1, lon1] = Math.abs(minx) <= 180 ? [miny, minx] : mercatorToLatLng(minx, miny);
        const [lat2, lon2] = Math.abs(maxx) <= 180 ? [maxy, maxx] : mercatorToLatLng(maxx, maxy);
        return window.Cesium.Rectangle.fromDegrees(lon1, lat1, lon2, lat2);
      }
    } catch (e) {}
    return null;
  };

  // --- 2. Discovery & Rendering ---
  useEffect(() => {
    const Cesium = window.Cesium;
    if (!viewer || !Cesium) return;
    let isMounted = true;

    const loadLayers = async () => {
      removeAllLayers();
      tilesRequested.current = 0;
      tilesFinished.current = 0;

      if (!latestSurvey?.location_name || !latestSurvey?.survey_time) {
        window.dispatchEvent(new CustomEvent("ndvi-layers-discovered", { detail: { layer_names: [] } }));
        return;
      }

      try {
        const params = new URLSearchParams({
          location_name: String(latestSurvey.location_name),
          survey_time: toDateKey(latestSurvey.survey_time),
        });

        const r = await fetch(`${apiBase}/ndvi/tiles-discovery?${params.toString()}`);
        if (!isMounted || !r.ok) return;
        const json = await r.json();
        const foundCandidates = Array.isArray(json.found) ? json.found : [];

        // Parallel processing of metadata and layers
        const layerPromises = foundCandidates.map(async (cand, index) => {
          const tileUrl = buildNdviUrl(cand, latestSurvey, index);
          if (!tileUrl) return null;

          const layerName = cand.layer_slug || cand.layer_name || `NDVI ${index + 1}`;
          const uniqueId = `auto-ndvi-${layerName}-${index}`;
          const provider = createNdviProvider(tileUrl, currentLut, layerName);

          const layer = viewer.imageryLayers.addImageryProvider(provider);
          layer.alpha = 0.85;
          layer.magnificationFilter = Cesium.TextureMagnificationFilter.NEAREST;
          layer.minificationFilter = Cesium.TextureMinificationFilter.LINEAR;
          
          const bounds = await fetchLayerMetadata(tileUrl);
          return { id: uniqueId, layer, name: layerName, tileUrl, bounds };
        });

        const results = (await Promise.all(layerPromises)).filter(Boolean);
        if (!isMounted) return;

        activeLayersRef.current = results;

        window.dispatchEvent(new CustomEvent("ndvi-layers-discovered", {
          detail: {
            location: latestSurvey.location_name,
            survey_time: latestSurvey.survey_time,
            layer_names: results.map(r => r.name),
            candidates: foundCandidates
          }
        }));

        const firstBounds = results.find(l => l.bounds)?.bounds;
        if (firstBounds) viewer.camera.flyTo({ destination: firstBounds, duration: 1.5 });
        viewer.scene.requestRender();

      } catch (err) { console.error("[NdviCesium] Error:", err); }
    };

    loadLayers();
    return () => { isMounted = false; removeAllLayers(); };
  }, [viewer, latestSurvey, apiBase, createNdviProvider, currentLut, removeAllLayers]);

  // --- 3. Handle External Events (Opacity, Toggle, Parameters) ---
  useEffect(() => {
    if (!viewer) return;

    const handleOpacity = (ev) => {
      const { id, opacity } = ev.detail || {};
      const target = activeLayersRef.current.find(item => item.id === id || item.name === id);
      if (target?.layer) {
        target.layer.alpha = Number(opacity);
        viewer.scene.requestRender();
      }
    };

    const handleToggle = (ev) => {
      const { id, visible } = ev.detail || {};
      const target = activeLayersRef.current.find(item => item.id === id || item.name === id);
      if (target?.layer) {
        target.layer.show = !!visible;
        if (visible) viewer.imageryLayers.raiseToTop(target.layer);
        viewer.scene.requestRender();
      }
    };

    const handleNdviParams = (ev) => {
      const { min, max } = ev.detail || {};
      if (min !== undefined) setFilterMin(Number(min));
      if (max !== undefined) setFilterMax(Number(max));
    };

    window.addEventListener("map-set-opacity", handleOpacity);
    window.addEventListener("map-toggle-layer", handleToggle);
    window.addEventListener("ndvi-set-params", handleNdviParams);

    // Maintenance interval for Z-Index
    const zIndexInterval = setInterval(() => {
      if (!viewer || viewer.isDestroyed()) return;
      activeLayersRef.current.forEach(item => {
        if (item.layer?.show && !item.layer.isDestroyed()) {
          viewer.imageryLayers.raiseToTop(item.layer);
        }
      });
    }, 2000);

    return () => {
      clearInterval(zIndexInterval);
      window.removeEventListener("map-set-opacity", handleOpacity);
      window.removeEventListener("map-toggle-layer", handleToggle);
      window.removeEventListener("ndvi-set-params", handleNdviParams);
    };
  }, [viewer]);

  // --- 4. Hover Sampling ---
  useEffect(() => {
    if (!viewer) return;
    const Cesium = window.Cesium;
    const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    let debounceTimer = null;

    const sampleTileValue = async (cartesian) => {
      const activeItem = [...activeLayersRef.current].reverse().find(i => i.layer?.show && i.tileUrl);
      if (!activeItem) {
        window.dispatchEvent(new CustomEvent("ndvi-hover-value", { detail: { value: null } }));
        return;
      }

      const cartographic = Cesium.Cartographic.fromCartesian(cartesian);
      const lat = Cesium.Math.toDegrees(cartographic.latitude);
      const lon = Cesium.Math.toDegrees(cartographic.longitude);
      const zoom = 19; const n = Math.pow(2, zoom);
      const x = Math.floor((lon + 180) / 360 * n);
      const latRad = lat * Math.PI / 180;
      const yRaw = Math.floor((1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2 * n);
      const y = USE_TMS ? (n - yRaw - 1) : yRaw;

      const tileUrl = activeItem.tileUrl.replace("{z}", zoom).replace("{x}", x).replace("{y}", y);

      try {
        const resp = await fetch(tileUrl);
        const blob = await resp.blob();
        const imgBitmap = await createImageBitmap(blob);
        if (!hoverCanvasRef.current) hoverCanvasRef.current = document.createElement("canvas");
        const cvs = hoverCanvasRef.current;
        cvs.width = 256; cvs.height = 256;
        const ctx = cvs.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(imgBitmap, 0, 0);

        const px = Math.floor(((lon - ((x / n) * 360 - 180)) / (360 / n)) * 256);
        const py = Math.floor((((1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2 * n) - yRaw) * 256);

        const pData = ctx.getImageData(px, py, 1, 1).data;
        const val = (pData[3] !== 0 && pData[0] !== 255) 
          ? DEFAULT_MIN_NDVI + (pData[0] / 254.0) * (DEFAULT_MAX_NDVI - DEFAULT_MIN_NDVI) 
          : null;

        window.dispatchEvent(new CustomEvent("ndvi-hover-value", { detail: { value: val } }));
      } catch (err) {
        window.dispatchEvent(new CustomEvent("ndvi-hover-value", { detail: { value: null } }));
      }
    };

    handler.setInputAction((movement) => {
      const cartesian = viewer.camera.pickEllipsoid(movement.endPosition);
      if (!cartesian) return;
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => sampleTileValue(cartesian), 50);
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    return () => { handler.destroy(); if (debounceTimer) clearTimeout(debounceTimer); };
  }, [viewer]);

  return (
    <>
      {loading && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.container}>
            <div style={modalStyles.title}>Processing Maps...</div>
            <div style={modalStyles.barContainer}>
              <div style={{ ...modalStyles.barFill, width: `${loadProgress}%` }} />
            </div>
            <div style={modalStyles.status}>{loadProgress}% Complete</div>
          </div>
        </div>
      )}
    </>
  );
}

// --- Styles for Modal ---
const modalStyles = {
  overlay: {
    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center',
    alignItems: 'center', zIndex: 10000, pointerEvents: 'none'
  },
  container: {
    backgroundColor: '#1e1e1e', border: '1px solid #444', padding: '24px',
    borderRadius: '12px', width: '300px', textAlign: 'center', color: 'white',
    boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
  },
  title: { fontSize: '16px', fontWeight: '600', marginBottom: '16px' },
  barContainer: { width: '100%', height: '8px', backgroundColor: '#333', borderRadius: '4px', overflow: 'hidden' },
  barFill: { height: '100%', backgroundColor: '#2ecc71', transition: 'width 0.3s ease-out' },
  status: { marginTop: '12px', fontSize: '12px', color: '#999' }
};
